import { useEffect, useMemo, useState } from 'react';
import { ViewModeFieldset } from '@/components/ViewModeFieldset';
import { useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Pencil, Plus } from 'lucide-react';
import { AddButton, Field, LiveScreen, QueryState, Select, StatusPill, formatDate, useFormErrors, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { DropZone } from '@/components/DropZone';
import { RecordDrawer } from '@/components/RecordDrawer';
import { ValidityBadge } from '@/components/ValidityBadge';
import { StatusChanger } from '@/components/StatusChanger';
import { useRows } from '@/hooks/useResource';
import { createRow, saveRow } from '@/api/resources';
import { signedUrlsFor, uploadDocument } from '@/api/documents';
import { useAuth } from '@/auth/AuthProvider';
import { can, normaliseIdentifier } from '@/auth/session';
import { statusStats } from '@/lib/recordStatus';
import { toast } from '@/lib/toast';
import { GeographyPicker } from '@/screens/parts/GeographyPicker';
import { WorkerDrawer, skillLabel, type WorkerRow } from '@/screens/parts/WorkerDrawer';

export interface ContractorRow {
  id: string;
  contractor_code: string | null;
  name: string;
  contractor_type: string;
  legal_form: string;
  contact_person_name: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  village_id: string | null;
  village_name: string | null;
  gstin: string | null;
  pan: string | null;
  licence_no: string | null;
  licence_type: string | null;
  licence_issued_by: string | null;
  licence_valid_from: string | null;
  licence_valid_to: string | null;
  licence_max_workers: number | null;
  licence_document_id: string | null;
  epf_code: string | null;
  esic_code: string | null;
  notes: string | null;
  status: string;
  licence_state: 'valid' | 'expiring' | 'expired' | 'none';
  active_workers: number;
}

const TYPES = [
  { id: 'labour', label: 'Labour supply' }, { id: 'harvesting', label: 'Harvesting / felling' },
  { id: 'transport', label: 'Transport' }, { id: 'nursery', label: 'Nursery' },
  { id: 'processing', label: 'Processing' }, { id: 'other', label: 'Other' },
];
const LEGAL_FORMS = [
  { id: 'individual', label: 'Individual' }, { id: 'proprietorship', label: 'Proprietorship' },
  { id: 'partnership', label: 'Partnership' }, { id: 'company', label: 'Company' }, { id: 'llp', label: 'LLP' },
  { id: 'cooperative', label: 'Cooperative' }, { id: 'other', label: 'Other' },
];
const LICENCE_TYPES = [
  { id: '', label: '--' }, { id: 'clra', label: 'Contract labour licence (CLRA)' }, { id: 'trade', label: 'Trade licence' },
  { id: 'shops_establishment', label: 'Shops & establishment' }, { id: 'forest_transit', label: 'Forest transit / felling permit' },
  { id: 'other', label: 'Other' },
];
const LICENCE_LABEL = { valid: 'Valid', expiring: 'Expires within 30 days', expired: 'Expired', none: 'No licence' } as const;

const label = (list: Array<{ id: string; label: string }>, id?: string | null) => list.find((entry) => entry.id === id)?.label ?? id ?? '--';

/**
 * Contractor inventory (owner, 2026-09-21): who supplies labour and services on the farms, their
 * licence and its validity, and the workers engaged through them.
 */
export default function ContractorsScreen() {
  const { profile } = useAuth();
  const [stat, setStat] = useState<string | null>(null);
  const [open, setOpen] = useState<ContractorRow | 'new' | null>(null);
  const mayAdd = can(profile, 'party.register', 'create');
  const rows = useRows<ContractorRow>('contractors', { orderBy: { column: 'name' } });

  const STATS = useMemo(() => [
    { key: 'all', label: 'Contractors', match: () => true, tone: 'neutral' as const },
    { key: 'expiring', label: 'Licence expiring', tone: 'warn' as const, match: (row: ContractorRow) => row.licence_state === 'expiring' },
    { key: 'expired', label: 'Licence expired', tone: 'error' as const, match: (row: ContractorRow) => row.licence_state === 'expired' },
    { key: 'none', label: 'No licence', tone: 'warn' as const, match: (row: ContractorRow) => row.licence_state === 'none' },
    ...statusStats<ContractorRow>((row) => row.status).filter((entry) => entry.key !== 'withdrawn'),
  ], []);
  const visible = useMemo(() => applyStat(rows.data ?? [], STATS, stat), [rows.data, STATS, stat]);

  const columns: Array<Column<ContractorRow>> = [
    { key: 'name', label: 'Contractor', render: (row) => (
      <span className="cell-stack"><strong>{row.name}</strong><small className="muted">{row.contractor_code}</small></span>
    ) },
    { key: 'contractor_type', label: 'Type', render: (row) => label(TYPES, row.contractor_type) },
    { key: 'contact_person_name', label: 'Contact', render: (row) => [row.contact_person_name, row.phone].filter(Boolean).join(' · ') || '--' },
    { key: 'licence_no', label: 'Licence', render: (row) => row.licence_no ?? '--' },
    { key: 'licence_valid_to', label: 'Valid to', render: (row) => (row.licence_valid_to ? <ValidityBadge from={row.licence_valid_from} to={row.licence_valid_to} /> : '--') },
    { key: 'licence_state', label: 'Licence', render: (row) => <span className={`licence-state ${row.licence_state}`}>{LICENCE_LABEL[row.licence_state]}</span> },
    { key: 'active_workers', label: 'Workers', align: 'right', render: (row) => (
      `${row.active_workers}${row.licence_max_workers ? ` / ${row.licence_max_workers}` : ''}`
    ) },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
  ];

  return (
    <LiveScreen
      actions={<AddButton disabled={!mayAdd} label="Add contractor" onClick={() => setOpen('new')}
                          title={mayAdd ? undefined : 'Your role cannot add contractors.'} />}
      subtitle="Contractors supplying labour and services, their licences, and the workers engaged through them."
      title="Contractors"
    >
      <StatCards active={stat} onSelect={setStat} rows={rows.data ?? []} secondary={{ sum: (row) => row.active_workers, unit: 'workers', digits: 0 }} stats={STATS} />
      <QueryState empty="No contractors yet." query={rows}>
        <DataGrid columns={columns} exportName="contractors" onRowClick={setOpen} rows={visible}
                  screenKey="party.register" searchPlaceholder="Name, code, licence, contact" />
      </QueryState>
      {open && <ContractorDrawer editing={open === 'new' ? null : open} key={open === 'new' ? 'new' : open.id} onClose={() => setOpen(null)} />}
    </LiveScreen>
  );
}

function ContractorDrawer({ editing, onClose }: { editing: ContractorRow | null; onClose: () => void }) {
  const mayWrite = can(useAuth().profile, 'party.register', editing ? 'update' : 'create');
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const { errors, check, clear } = useFormErrors();
  const [tab, setTab] = useState<'details' | 'workers'>('details');
  const [addingWorker, setAddingWorker] = useState<WorkerRow | 'new' | null>(null);
  const [licenceFile, setLicenceFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  // A saved record opens read-only; Edit unlocks it (owner, 2026-09-27).
  const [viewing, setViewing] = useState(Boolean(editing));
  const editingId = editing?.id;
  useEffect(() => { setViewing(Boolean(editingId)); }, [editingId]);
  const [form, setForm] = useState(() => ({
    name: editing?.name ?? '', contractor_type: editing?.contractor_type ?? 'labour', legal_form: editing?.legal_form ?? 'proprietorship',
    contact_person_name: editing?.contact_person_name ?? '', phone: editing?.phone ?? '', email: editing?.email ?? '',
    address: editing?.address ?? '', village_id: editing?.village_id ?? '', gstin: editing?.gstin ?? '', pan: editing?.pan ?? '',
    licence_no: editing?.licence_no ?? '', licence_type: editing?.licence_type ?? '', licence_issued_by: editing?.licence_issued_by ?? '',
    licence_valid_from: editing?.licence_valid_from ?? '', licence_valid_to: editing?.licence_valid_to ?? '',
    licence_max_workers: editing?.licence_max_workers?.toString() ?? '', epf_code: editing?.epf_code ?? '',
    esic_code: editing?.esic_code ?? '', notes: editing?.notes ?? '',
  }));
  const workers = useRows<WorkerRow>('workers', { filters: { contractor_id: editing?.id }, orderBy: { column: 'full_name' } }, Boolean(editing));

  async function openLicence() {
    if (!editing?.licence_document_id) return;
    const url = (await signedUrlsFor([editing.licence_document_id]))[editing.licence_document_id];
    if (url) window.open(url, '_blank', 'noopener,noreferrer'); else toast.error('The licence could not be opened.');
  }

  async function save() {
    const phone = form.phone.trim() ? normaliseIdentifier('sms', form.phone.trim()) : null;
    const gstin = form.gstin.trim().toUpperCase();
    const pan = form.pan.trim().toUpperCase();
    const ok = check({
      name: form.name.trim() ? undefined : 'Give the contractor\'s name.',
      village_id: form.village_id ? undefined : 'Where is the contractor based? It decides who can see the record.',
      phone: phone && 'error' in phone ? phone.error : undefined,
      gstin: !gstin || /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin) ? undefined : 'That is not a valid GSTIN.',
      pan: !pan || /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan) ? undefined : 'That is not a valid PAN.',
      licence_valid_to: form.licence_no.trim() && !form.licence_valid_to ? 'A licence needs its valid-to date.'
        : form.licence_valid_to && form.licence_valid_from && form.licence_valid_to < form.licence_valid_from
          ? 'The licence cannot end before it starts.' : undefined,
    });
    if (!ok) return;
    setSaving(true);
    try {
      const values = {
        name: form.name.trim(), contractor_type: form.contractor_type, legal_form: form.legal_form,
        contact_person_name: form.contact_person_name.trim() || null, phone: phone && 'value' in phone ? phone.value : null,
        email: form.email.trim() || null, address: form.address.trim() || null, village_id: form.village_id || null, gstin: gstin || null, pan: pan || null,
        licence_no: form.licence_no.trim() || null, licence_type: form.licence_type || null,
        licence_issued_by: form.licence_issued_by.trim() || null, licence_valid_from: form.licence_valid_from || null,
        licence_valid_to: form.licence_valid_to || null,
        licence_max_workers: form.licence_max_workers ? Number(form.licence_max_workers) : null,
        epf_code: form.epf_code.trim() || null, esic_code: form.esic_code.trim() || null, notes: form.notes.trim() || null,
      };
      const saved = editing ? await saveRow<ContractorRow>('contractors', editing.id, values) : await createRow<ContractorRow>('contractors', values);
      if (licenceFile && profile?.organization) {
        try {
          const document = await uploadDocument({ file: licenceFile, bucket: 'kyc-documents', organizationId: profile.organization.id,
                                                  ownerId: saved.id, issuedDate: form.licence_valid_from || null, expiryDate: form.licence_valid_to || null });
          await saveRow('contractors', saved.id, { licence_document_id: document.id });
        } catch (error) {
          toast.error(`Saved, but the licence file was not: ${error instanceof Error ? error.message : 'upload failed'}.`);
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['contractors'] });
      toast.success(editing ? 'Saved.' : 'Contractor added.');
      clear();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <RecordDrawer
      footer={
        <>
          {viewing ? (
            <>
              <button onClick={onClose} type="button">Close</button>
              {tab === 'details' && mayWrite && <button className="primary" onClick={() => setViewing(false)} type="button"><Pencil size={14} /> Edit</button>}
            </>
          ) : (
            <>
              <button className="cancel" onClick={onClose} type="button">Cancel</button>
              {tab === 'details' && mayWrite && (
                <button className="primary" disabled={saving} onClick={() => void save()} type="button">
                  {saving ? 'Saving...' : editing ? 'Save changes' : 'Add contractor'}
                </button>
              )}
            </>
          )}
        </>
      }
      onClose={onClose}
      open
      record={editing ? { table: 'ops.contractors', id: editing.id, invalidate: ['contractors'] } : null}
      sections={editing ? {
        items: [
          { id: 'details', label: 'Details & licence' },
          { id: 'workers', label: `Workers (${workers.data?.length ?? 0})` },
        ],
        active: tab,
        onChange: (id) => setTab(id as 'details' | 'workers'),
      } : undefined}
      subtitle={editing?.contractor_code ?? 'Labour or service contractor'}
      title={editing ? editing.name : 'Add contractor'}
      wide
    >

      {tab === 'details' && (
        <ViewModeFieldset viewing={viewing}>
          {editing && <StatusChanger current={editing.status} entity="contractor" id={editing.id} invalidate={['contractors']} screenKey="party.register" />}
          <Field error={errors.name} label="Name" required>
            <input onChange={(event) => setForm({ ...form, name: event.target.value })} value={form.name} />
          </Field>
          <div className="field-row">
            <Field label="Type"><Select onChange={(value) => setForm({ ...form, contractor_type: value })} options={TYPES} value={form.contractor_type} /></Field>
            <Field label="Legal form"><Select onChange={(value) => setForm({ ...form, legal_form: value })} options={LEGAL_FORMS} value={form.legal_form} /></Field>
          </div>
          <div className="field-row">
            <Field label="Contact person"><input onChange={(event) => setForm({ ...form, contact_person_name: event.target.value })} value={form.contact_person_name} /></Field>
            <Field error={errors.phone} label="Phone"><input inputMode="tel" onChange={(event) => setForm({ ...form, phone: event.target.value })} value={form.phone} /></Field>
          </div>
          <div className="field-row">
            <Field label="Email"><input inputMode="email" onChange={(event) => setForm({ ...form, email: event.target.value })} value={form.email} /></Field>
            <Field label="Address"><input onChange={(event) => setForm({ ...form, address: event.target.value })} value={form.address} /></Field>
          </div>
          <div className="field-row">
            <Field error={errors.gstin} label="GSTIN"><input onChange={(event) => setForm({ ...form, gstin: event.target.value })} value={form.gstin} /></Field>
            <Field error={errors.pan} label="PAN"><input onChange={(event) => setForm({ ...form, pan: event.target.value })} value={form.pan} /></Field>
          </div>
          <GeographyPicker error={errors.village_id} label="Based in (village)" onChange={(villageId) => setForm({ ...form, village_id: villageId })} value={form.village_id} />
          <div className="section-heading"><h3>Licence</h3></div>
          <div className="field-row">
            <Field label="Licence type"><Select onChange={(value) => setForm({ ...form, licence_type: value })} options={LICENCE_TYPES} value={form.licence_type} /></Field>
            <Field label="Licence number"><input onChange={(event) => setForm({ ...form, licence_no: event.target.value })} value={form.licence_no} /></Field>
          </div>
          <div className="field-row">
            <Field label="Issued by"><input onChange={(event) => setForm({ ...form, licence_issued_by: event.target.value })} placeholder="e.g. Assistant Labour Commissioner" value={form.licence_issued_by} /></Field>
            <Field hint="Active workers above this are refused." label="Workers allowed">
              <input inputMode="numeric" onChange={(event) => setForm({ ...form, licence_max_workers: event.target.value })} value={form.licence_max_workers} />
            </Field>
          </div>
          <div className="field-row">
            <Field label="Valid from"><input onChange={(event) => setForm({ ...form, licence_valid_from: event.target.value })} type="date" value={form.licence_valid_from} /></Field>
            <Field error={errors.licence_valid_to} label="Valid to" required={Boolean(form.licence_no.trim())}><input onChange={(event) => setForm({ ...form, licence_valid_to: event.target.value })} type="date" value={form.licence_valid_to} /></Field>
          </div>
          <Field hint={licenceFile ? `${licenceFile.name} will be uploaded.` : 'A photo or PDF of the licence.'} label="Licence document">
            <DropZone accept="image/*,application/pdf" buttonLabel={licenceFile || editing?.licence_document_id ? 'Replace file' : 'Choose file'}
                      label="Drag the licence here, or" onFile={setLicenceFile} />
          </Field>
          {editing?.licence_document_id && (
            <button onClick={() => void openLicence()} style={{ justifySelf: 'start' }} type="button"><ExternalLink size={14} /> Open licence on file</button>
          )}
          <div className="field-row">
            <Field label="EPF code"><input onChange={(event) => setForm({ ...form, epf_code: event.target.value })} value={form.epf_code} /></Field>
            <Field label="ESIC code"><input onChange={(event) => setForm({ ...form, esic_code: event.target.value })} value={form.esic_code} /></Field>
          </div>
          <Field label="Notes"><textarea onChange={(event) => setForm({ ...form, notes: event.target.value })} rows={2} value={form.notes} /></Field>
        </ViewModeFieldset>
      )}

      {tab === 'workers' && editing && (
        <>
          <div className="section-heading">
            <h3>Workers engaged through {editing.name}</h3>
            <button onClick={() => setAddingWorker('new')} type="button"><Plus size={15} /> Add worker</button>
          </div>
          <QueryState empty="No workers yet." query={workers}>
            <table className="simple-table">
              <thead><tr><th>Worker</th><th>Skill</th><th>Age</th><th>PPE</th><th>Safety training</th><th>Status</th></tr></thead>
              <tbody>
                {(workers.data ?? []).map((row) => (
                  <tr className="clickable" key={row.id} onClick={() => setAddingWorker(row)}>
                    <td><strong>{row.full_name}</strong> <small className="muted">{row.worker_code}</small>{!row.is_active && <small className="muted"> · left</small>}</td>
                    <td>{skillLabel(row.skill)}</td>
                    <td>{row.age_years ?? '--'}</td>
                    <td>{row.ppe_issued ? 'Yes' : <span className="field-error">No</span>}</td>
                    <td>{row.safety_trained_on ? formatDate(row.safety_trained_on) : <span className="field-error">Not trained</span>}</td>
                    <td><StatusPill value={row.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </QueryState>
          {addingWorker && (
            <WorkerDrawer contractorId={editing.id} editing={addingWorker === 'new' ? null : addingWorker}
                          key={addingWorker === 'new' ? 'new' : addingWorker.id} onClose={() => setAddingWorker(null)} />
          )}
        </>
      )}
    </RecordDrawer>
  );
}
